import React, { useEffect, useState } from 'react';
import { API_URL } from './config';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import LoginPage from './views/LoginPage';
import RegisterPage from './views/RegisterPage';
import VerifyEmailPage from './views/VerifyEmailPage';
import DashboardPage from './views/DashboardPage';
import ProfilePage from './views/ProfilePage';
import CatchEntryPage from './views/CatchEntryPage';
import CatchMapPage from './views/CatchMapPage';
import GroupPage from './views/GroupPage';
import AdminUsersPage from './views/AdminUsersPage';
import AdminGroupsPage from './views/AdminGroupsPage';
import AdminSpeciesPage from './views/AdminSpeciesPage';
import TacklePage from './views/TacklePage';
import TabNav from './TabNav';
import BuddiesPage from './views/BuddiesPage';
import { useAuth } from './AuthContext';


function RoutesWithAutoRegister({ isAuthenticated }) {
  const [noUsers, setNoUsers] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const groups = Array.isArray(user?.groups) ? user.groups : [];
  const isSiteAdmin = groups.some((g) => g.role === 'admin' && (g.group_name || '').toLowerCase() === 'admins');
  useEffect(() => {
    // Only check initial user-count for unauthenticated users; once logged in,
    // never force-redirect them to the register page.
    if (isAuthenticated) {
      setNoUsers(false);
      return;
    }
    fetch(`${API_URL}/user-count`)
      .then(res => (res.ok ? res.json() : { user_count: 0 }))
      .then(data => setNoUsers(typeof data.user_count === 'number' && data.user_count === 0))
      .catch(() => setNoUsers(false));
  }, [location.pathname, isAuthenticated]);
  useEffect(() => {
    // Do not auto-redirect logged-in users to /register even if user-count
    // endpoint misreports zero users.
    if (isAuthenticated) return;
    if (noUsers && location.pathname !== '/register') {
      navigate('/register', { replace: true });
    }
    // Removed: Auto-redirect from /register to /login when users exist
    // Users should be able to register even if other users exist
  }, [noUsers, location.pathname, navigate, isAuthenticated]);
  return (
    <>
      <div>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/dashboard" element={isAuthenticated ? <DashboardPage /> : <Navigate to="/login" />} />
          <Route path="/profile/:userId" element={isAuthenticated ? <ProfilePage /> : <Navigate to="/login" />} />
          <Route path="/catch" element={isAuthenticated ? <CatchEntryPage /> : <Navigate to="/login" />} />
          <Route path="/catch-map" element={isAuthenticated ? <CatchMapPage /> : <Navigate to="/login" />} />
          <Route path="/group/:groupId" element={isAuthenticated ? <GroupPage /> : <Navigate to="/login" />} />
          {/** Legacy leaderboard route removed; dashboard shows leaderboard **/}
          <Route path="/admin/users" element={isAuthenticated ? (isSiteAdmin ? <AdminUsersPage /> : <Navigate to="/dashboard" />) : <Navigate to="/login" />} />
          <Route path="/admin/groups" element={isAuthenticated ? (isSiteAdmin ? <AdminGroupsPage /> : <Navigate to="/dashboard" />) : <Navigate to="/login" />} />
          <Route path="/admin/species" element={isAuthenticated ? (isSiteAdmin ? <AdminSpeciesPage /> : <Navigate to="/dashboard" />) : <Navigate to="/login" />} />
          <Route path="/tackle" element={isAuthenticated ? <TacklePage /> : <Navigate to="/login" />} />
          <Route path="/buddies" element={isAuthenticated ? <BuddiesPage /> : <Navigate to="/login" />} />
          <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : (noUsers ? "/register" : "/login") } />} />
        </Routes>
      </div>
      {isAuthenticated && location.pathname !== '/login' && location.pathname !== '/register' ? (
        <TabNav />
      ) : null}
    </>
  );
}

export default function AppRoutes({ isAuthenticated }) {
  return (
    <BrowserRouter>
      <RoutesWithAutoRegister isAuthenticated={isAuthenticated} />
    </BrowserRouter>
  );
}
